import { createFileRoute } from '@tanstack/react-router'
import { StaticNav } from '@/components/static-nav'
import { StaticFooter } from '@/components/static-footer'

export const Route = createFileRoute('/privacy')({
  component: PrivacyRoute,
})

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-semibold text-slate-950 mb-4">{title}</h2>
      <div className="space-y-4 text-sm leading-relaxed text-slate-600">{children}</div>
    </section>
  )
}

function PrivacyRoute() {
  return (
    <>
      <StaticNav />
      <main className="min-h-screen bg-[#fafafa] pt-14">

        {/* Page header */}
        <div className="bg-white border-b border-slate-200 px-6 py-16">
          <div className="mx-auto max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Legal</p>
            <h1 className="text-4xl font-semibold tracking-tight text-slate-950">Privacy Policy</h1>
            <p className="mt-3 text-sm text-slate-500">Last updated: April 2026</p>

            {/* Short version callout */}
            <div className="mt-8 flex items-start gap-4 rounded-2xl bg-slate-50 border border-slate-200 p-5">
              <span className="text-xl shrink-0 mt-0.5">🔒</span>
              <div>
                <p className="text-sm font-semibold text-slate-900 mb-1">The short version</p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  We use email metadata to organise your inbox. When you open a message, we fetch its body and attachments from Gmail so you can read it in Winbox Zero. If you choose to reply, we send the message you write through Gmail. If you choose to save an attachment to Google Drive, we upload only that selected file to your Drive. We do not use email body content for AI classification.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-16">
          <div className="mx-auto max-w-3xl space-y-16">

            {/* What we access / don't */}
            <section>
              <h2 className="text-xl font-semibold text-slate-950 mb-6">What we access — and what we never touch</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-4">We access</p>
                  <ul className="space-y-2.5 text-sm text-slate-700">
                    {[
                      'Sender name and email address',
                      'Subject line',
                      'Date and timestamp',
                      'Labels you have applied in Gmail',
                      'Read / unread and starred status',
                      'Thread structure (who replied to whom)',
                      'Email body and attachments when you open a message',
                      'Replies you choose to send from Winbox Zero',
                      'Attachments you choose to save to Google Drive',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <span className="mt-0.5 text-emerald-600 shrink-0">✓</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-4">We never touch</p>
                  <ul className="space-y-2.5 text-sm text-slate-600">
                    {[
                      'Contacts or calendar data',
                      'Email body content for AI classification',
                      'Attachments for AI classification',
                      'Any data outside Gmail unless you explicitly connect Google Drive and choose to save a file there',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <span className="mt-0.5 text-slate-400 shrink-0">✗</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* 1. Information we collect */}
            <Section title="1. Information we collect">
              <p>
                When you connect your Gmail account, we receive an OAuth token that grants us access to the Gmail features needed to run Winbox Zero. We use this to fetch the sender, subject, date, labels, thread structure, and, when you open a message, the message body and attachments required to display it in the app.
              </p>
              <p>
                We also collect standard account information — your name and email address — as provided by Google at sign-in. This is used solely to identify your account.
              </p>
              <p>
                If you use reply inside Winbox Zero, we send the text you write through your Gmail account and keep the reply within the original thread. We do not use your reply text to train shared models.
              </p>
              <p>
                If you connect Google Drive and click save on an attachment, we request Google Drive permission at that moment and upload only the file you selected into the Winbox Zero folder in your Drive. We do not export attachments to Drive unless you explicitly ask us to.
              </p>
              <p>
                We collect anonymised product usage data (which features you use, how often) to understand how to improve the service. This data cannot be linked back to the content of any specific email.
              </p>
            </Section>

            {/* 2. How we use your information */}
            <Section title="2. How we use your information">
              <p>We use your information exclusively to provide and improve the Winbox Zero service. Specifically:</p>
              <ul className="list-none space-y-3 pl-0">
                {[
                  ['Organise your inbox', 'We analyse metadata patterns — who you reply to, which senders you ignore, how often newsletters are opened — to surface the emails that need your attention and suggest what to clean up.'],
                  ['Build your personal model', 'We learn from your corrections. If you move an email to a different column, that signal updates your model immediately and permanently.'],
                  ['Send service updates', 'We may email you about significant changes to the product or your account. You can opt out of non-essential emails at any time.'],
                  ['Process payments', 'If you upgrade to a paid plan, billing is handled by Stripe. We never see or store your card details.'],
                ].map(([title, body]) => (
                  <li key={title} className="rounded-xl border border-slate-200 bg-white p-4">
                    <p className="font-medium text-slate-900 mb-1">{title}</p>
                    <p className="text-slate-600">{body}</p>
                  </li>
                ))}
              </ul>
              <p className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-amber-800">
                <strong>We will never</strong> sell your data, use it to train general-purpose AI models, share it with advertisers, or use it for any purpose outside of running this service for you.
              </p>
            </Section>

            {/* 3. AI processing */}
            <Section title="3. AI and machine learning">
              <p>
                Winbox Zero uses AI to classify and prioritise your emails. This classification runs on email metadata only — sender, subject, and timestamps. Email body content and reply text are not sent to any AI system for classification.
              </p>
              <p>
                We use Groq to run fast inference on metadata signals. The data sent to Groq contains no personally identifying information beyond what is already in a typical email header (sender address, subject line). Groq processes this data on our behalf under a data processing agreement and does not retain it for their own purposes.
              </p>
              <p>
                Your personal inbox model — the learned preferences specific to your account — is stored separately and is never shared across users or used to train shared models.
              </p>
            </Section>

            {/* 4. Third-party services */}
            <Section title="4. Third-party services">
              <p>We work with a small number of carefully chosen providers:</p>
              <div className="overflow-hidden rounded-2xl border border-slate-200">
                {[
                  { name: 'Google OAuth', purpose: 'Authentication, Gmail access, and optional Google Drive attachment saves', link: false },
                  { name: 'Cloudflare', purpose: 'Hosting, edge network, and encrypted data storage', link: false },
                  { name: 'Groq', purpose: 'AI inference on email metadata (no body content)', link: false },
                  { name: 'Stripe', purpose: 'Payment processing for paid plans', link: false },
                ].map(({ name, purpose }, i, arr) => (
                  <div key={name} className={`flex items-start justify-between gap-4 px-5 py-4 bg-white ${i < arr.length - 1 ? 'border-b border-slate-100' : ''}`}>
                    <span className="font-medium text-slate-900 text-sm w-32 shrink-0">{name}</span>
                    <span className="text-slate-600 text-sm">{purpose}</span>
                  </div>
                ))}
              </div>
              <p>
                Each provider is bound by a data processing agreement. We do not use any advertising networks, analytics trackers, or data brokers.
              </p>
            </Section>

            {/* 5. Data retention */}
            <Section title="5. Data retention and deletion">
              <p>
                We retain your metadata and inbox model for as long as your account is active. If you revoke Gmail access, we stop syncing immediately. If you delete your account, all associated data — your metadata cache, learned preferences, and account record — is permanently deleted within 30 days.
              </p>
              <p>
                You can delete your account at any time from Settings → Account. No hoops, no retention emails, no re-engagement campaigns.
              </p>
            </Section>

            {/* 6. Your rights */}
            <Section title="6. Your rights">
              <p>You have the right to:</p>
              <ul className="space-y-2 pl-0 list-none">
                {[
                  'Access the personal data we hold about you',
                  'Request a copy of your data in a portable format',
                  'Request correction of inaccurate data',
                  'Request deletion of all your data',
                  'Revoke Gmail access at any time via your Google account settings',
                  'Opt out of non-essential product communications',
                ].map((right) => (
                  <li key={right} className="flex items-start gap-2 text-slate-700">
                    <span className="mt-0.5 text-slate-400 shrink-0">→</span>
                    {right}
                  </li>
                ))}
              </ul>
              <p>
                To exercise any of these rights, email us at <a href="mailto:support@winboxzero.com" className="text-slate-900 underline underline-offset-2">support@winboxzero.com</a>. We will respond within 30 days.
              </p>
            </Section>

            {/* 7. Security */}
            <Section title="7. Security">
              <p>
                All data is encrypted in transit (TLS 1.3) and at rest (AES-256). Your Gmail OAuth token is stored encrypted and scoped to the minimum permissions required. We conduct regular security reviews and follow responsible disclosure practices.
              </p>
              <p>
                If you discover a security vulnerability, please email <a href="mailto:support@winboxzero.com" className="text-slate-900 underline underline-offset-2">support@winboxzero.com</a>. We take all reports seriously and will respond within 48 hours.
              </p>
            </Section>

            {/* 8. Changes */}
            <Section title="8. Changes to this policy">
              <p>
                If we make material changes to this policy, we will notify you by email at least 14 days before the changes take effect. Minor clarifications (grammar, typos, better explanations of existing practices) will be updated without notice. The "last updated" date at the top of this page always reflects the most recent revision.
              </p>
            </Section>

            {/* Contact */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="text-base font-semibold text-slate-950 mb-1">Questions about your privacy?</h2>
              <p className="text-sm text-slate-600 mb-4">
                We are a small team and we read every email. If something in this policy is unclear, or if you have a request related to your data, reach out directly.
              </p>
              <a
                href="mailto:support@winboxzero.com"
                className="inline-flex items-center text-sm font-medium text-slate-900 underline underline-offset-2"
              >
                support@winboxzero.com
              </a>
            </div>

          </div>
        </div>
      </main>
      <StaticFooter />
    </>
  )
}
